import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { applyClasses, applyProps } from '../shared/js';
import Actions from './Actions';
import Drawer from './Drawer';
import Links from './Links';
import Logo from './Logo';
import Search from './Search';
import Subnav from './Subnav';
import Checkout from './Checkout';
import TranslateLink from './TranslateLink';
import {
  Button,
  Container,
  Typography,
  Grid,
  HTML,
  Icons,
  Modal,
  QuoteButton,
} from '../index';

import styles from './nav.module.scss';

const links = [
  {
    url: '/insurance',
    label: 'Insurance',
    dropdownLinks: [
      {
        url: '/auto',
        label: 'Auto',
      },
      {
        url: '/home',
        label: 'Home',
      },
      {
        url: '/condo',
        label: 'Condo',
      },
      {
        url: '/tenant',
        label: 'Tenant',
      },
      {
        url: '/landlord',
        label: 'Landlord',
      },
      {
        url: '/pet',
        label: 'Pet',
      },
    ],
  },
  {
    url: '/claims',
    label: 'Claims',
    dropdownLinks: [
      {
        url: '/auto-claims',
        label: 'Auto',
      },
      {
        url: '/home-claims',
        label: 'Home',
      },
      {
        url: '/pet-claims',
        label: 'Pet',
      },
    ],
  },
  {
    url: '/blog',
    label: 'Blog',
  },
];

const sublinks = [
  {
    url: '/faqs',
    label: 'FAQs',
  },
  {
    url: '/group-insurance',
    label: 'Group Insurance',
  },
  {
    url: '/sonnect-connect',
    label: 'Sonnet Connect',
  },
  {
    url: '/about-us',
    label: 'About Us',
  },
  {
    url: '/contact-us',
    label: 'Contact Us',
  },
];

const Nav = (props) => {
  const [navOpen, setNavOpen] = useState(false);
  const [navPinned, setNavPinned] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [translateDistance, setTranslateDistance] = useState(0);
  const navRef = useRef();
  const subnavRef = useRef();

  useEffect(() => {
    if (props.secondary) return;
    const subnavBottom = subnavRef.current.getBoundingClientRect().bottom;
    const handleScroll = () => {
      if (window.scrollY >= subnavBottom) {
        setNavPinned(true);
        setTranslateDistance(-1 * subnavBottom);
      } else {
        setNavPinned(false);
        setTranslateDistance(0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (props.secondary) {
    return <Checkout {...props} />;
  }

  return (
    <>
      <div
        ref={navRef}
        className={cn(
          styles.nav,
          { [styles.pinned]: navPinned },
          applyClasses({ ...props, desktopOnly: true }, styles)
        )}
        style={{ transform: `translateY(${translateDistance}px)` }}
        {...applyProps(props)}
      >
        <Grid className={styles.navInner}>
          <Grid.Row>
            <Subnav ref={subnavRef} links={sublinks}>
              <TranslateLink onClick={props.setLanguage} />
              <Search />
            </Subnav>
          </Grid.Row>
          <Grid.Row alignItemsEnd justifyContentBetween>
            <Grid.Column colAuto>
              <Grid.Row alignItemsEnd>
                <Grid.Column col4>
                  <Logo />
                </Grid.Column>
                <Grid.Column col8>
                  <Links links={links} />
                </Grid.Column>
              </Grid.Row>
            </Grid.Column>
            <Grid.Column colAuto>
              <Actions>
                <Button primary onClick={() => setQuoteModalOpen(true)}>
                  Get a Quote
                </Button>
                <Button href="/log-in" tertiary>
                  Log In
                </Button>
              </Actions>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
      <div
        className={cn(
          styles.nav,
          applyClasses({ ...props, tabletOnly: true }, styles)
        )}
      >
        <div className={styles.navInner}>
          <Logo onToggle={() => setNavOpen(!navOpen)} isOpen={navOpen} />
          <Actions>
            {!navOpen && (
              <>
                <Button
                  primary
                  hideOnMobile
                  onClick={() => setQuoteModalOpen(true)}
                >
                  Get a Quote
                </Button>
                <Button href="/log-in" tertiary hideOnMobile>
                  Log In
                </Button>
              </>
            )}
          </Actions>
          <Drawer isOpen={navOpen} onClose={() => setNavOpen(false)}>
            <Button primary fullWidth onClick={() => setQuoteModalOpen(true)}>
              Get a Quote
            </Button>
            <Links links={links} />
            <Subnav links={sublinks}>
              <TranslateLink />
              <Search />
            </Subnav>
          </Drawer>
        </div>
      </div>
      <Modal
        isOpen={quoteModalOpen}
        onRequestClose={() => setQuoteModalOpen(false)}
        narrow
      >
        <Container pXl>
          <Typography h2 bold center>
            Let&apos;s get insured
          </Typography>
          <Typography center>
            It only takes 5 minutes. What type of insurance are you looking for?
          </Typography>
          <QuoteButton.Container>
            <QuoteButton
              href="/quoting/auto-property"
              primary
              fullWidth
              title="Save up to 20%"
            >
              <HTML flex justifyCenter alignCenter>
                <div className={styles.buttonIcon}>
                  <Icons.Car />
                </div>
                <div className={styles.buttonIconSmall}>
                  <Icons.Plus />
                </div>
                <div className={styles.buttonIcon}>
                  <Icons.House />
                </div>
              </HTML>
              Home & Auto
            </QuoteButton>
            <QuoteButton href="/quoting/auto">
              <div className={styles.buttonIcon}>
                <Icons.Car />
              </div>
              Auto
            </QuoteButton>
            <QuoteButton href="/quoting/property">
              <div className={styles.buttonIcon}>
                <Icons.House />
              </div>
              Home
            </QuoteButton>
            <QuoteButton
              href="/quoting/pet"
              secondary
              border
              fullWidth
              title="NEW! Protect your furry loved one"
            >
              <div className={styles.buttonIcon}>
                <Icons.Paw />
              </div>
              Pet
            </QuoteButton>
          </QuoteButton.Container>
        </Container>
      </Modal>
    </>
  );
};

Nav.propTypes = {
  /** A style object forwarded to the main nav component */
  style: PropTypes.any,
  /** A class name forwarded to the main nav component */
  className: PropTypes.string,
  /** Render the quoting nav */
  secondary: PropTypes.bool,
  /** The callback function called when the language switch button is clicked */
  setLanguage: PropTypes.func,
  /** Whether or not the user is logged in */
  loggedIn: PropTypes.bool,
  /** A callback function called when clicking the log out button */
  onLogout: PropTypes.func,
  onCartClick: PropTypes.func,
  /** A callback function called when clicking the Logo button */
  onLogoClick: PropTypes.func,
  /** The links to add to the nav */
  navLinks: PropTypes.array,
};

Nav.defaultProps = {
  style: undefined,
  className: undefined,
  secondary: undefined,
  setLanguage: undefined,
};

Nav.Actions = Actions;
Nav.Drawer = Drawer;
Nav.Links = Links;
Nav.Logo = Logo;
Nav.Search = Search;
Nav.Subnav = Subnav;
Nav.TranslateLink = TranslateLink;
export default Nav;
